import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { PaymentService } from '../../services/payment';
import { safeArray } from '../../utils/money';
import { PaymentIntent, Order, Refund } from '../../types';

// 异步actions
export const fetchPaymentIntents = createAsyncThunk(
  'payment/fetchIntents',
  async (params: { page?: number; limit?: number; filters?: any }, { rejectWithValue }) => {
    try {
      const response = await PaymentService.listPaymentIntents(
        params.page,
        params.limit,
        params.filters
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取支付记录失败');
    }
  }
);

export const createPaymentIntent = createAsyncThunk(
  'payment/createIntent',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await PaymentService.createPaymentIntent(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '创建支付意图失败');
    }
  }
);

export const fetchOrders = createAsyncThunk(
  'payment/fetchOrders',
  async (params: { page?: number; limit?: number; filters?: any }, { rejectWithValue }) => {
    try {
      const response = await PaymentService.listOrders(
        params.page,
        params.limit,
        params.filters
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取订单列表失败');
    }
  }
);

export const createOrder = createAsyncThunk(
  'payment/createOrder',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await PaymentService.createOrder(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '创建订单失败');
    }
  }
);

export const fetchRefunds = createAsyncThunk(
  'payment/fetchRefunds',
  async (params: { page?: number; limit?: number; filters?: any }, { rejectWithValue }) => {
    try {
      const response = await PaymentService.listRefunds(
        params.page,
        params.limit,
        params.filters
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取退款记录失败');
    }
  }
);

interface PaymentState {
  paymentIntents: {
    data: PaymentIntent[];
    pagination: any;
    loading: boolean;
  };
  orders: {
    data: Order[];
    pagination: any;
    loading: boolean;
  };
  refunds: {
    data: Refund[];
    pagination: any;
    loading: boolean;
  };
  currentOrder: Order | null;
  creating: boolean;
  error: string | null;
}

const initialState: PaymentState = {
  paymentIntents: {
    data: [],
    pagination: null,
    loading: false,
  },
  orders: {
    data: [],
    pagination: null,
    loading: false,
  },
  refunds: {
    data: [],
    pagination: null,
    loading: false,
  },
  currentOrder: null,
  creating: false,
  error: null,
};

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentOrder: (state, action: PayloadAction<Order | null>) => {
      state.currentOrder = action.payload;
    },
    updateOrderStatus: (state, action: PayloadAction<{ id: string; status: string }>) => {
      const order = state.orders.data.find(o => o.id === action.payload.id);
      if (order) {
        order.status = action.payload.status as any;
      }
    },
  },
  extraReducers: (builder) => {
    // 获取支付意图
    builder
      .addCase(fetchPaymentIntents.pending, (state) => {
        state.paymentIntents.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentIntents.fulfilled, (state, action) => {
        state.paymentIntents.loading = false;
        state.paymentIntents.data = safeArray(action.payload.data);
        state.paymentIntents.pagination =
          action.payload.pagination || (action.payload as any).meta || null;
      })
      .addCase(fetchPaymentIntents.rejected, (state, action) => {
        state.paymentIntents.loading = false;
        state.error = action.payload as string;
      });

    // 创建支付意图
    builder
      .addCase(createPaymentIntent.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createPaymentIntent.fulfilled, (state) => {
        state.creating = false;
      })
      .addCase(createPaymentIntent.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      });

    // 获取订单
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.orders.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.orders.loading = false;
        state.orders.data = safeArray(action.payload.data);
        state.orders.pagination =
          action.payload.pagination || (action.payload as any).meta || null;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.orders.loading = false;
        state.error = action.payload as string;
      });

    // 创建订单
    builder
      .addCase(createOrder.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action: PayloadAction<Order>) => {
        state.creating = false;
        state.orders.data.unshift(action.payload);
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      });

    // 获取退款
    builder
      .addCase(fetchRefunds.pending, (state) => {
        state.refunds.loading = true;
        state.error = null;
      })
      .addCase(fetchRefunds.fulfilled, (state, action) => {
        state.refunds.loading = false;
        state.refunds.data = safeArray(action.payload.data);
        state.refunds.pagination =
          action.payload.pagination || (action.payload as any).meta || null;
      })
      .addCase(fetchRefunds.rejected, (state, action) => {
        state.refunds.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentOrder, updateOrderStatus } = paymentSlice.actions;
export default paymentSlice.reducer; 